// @flow
import JQuery from 'jquery';
import { get, map, forEach, endsWith } from 'lodash';
import moment from 'moment';
import Bugsnag from '@bugsnag/js';
import { lookup } from 'mime-to-extensions';
import { socialShareFile } from '../../redux/middleware/showPDF';
import { SHARE_TOAST_ID } from '../../redux/modules/context/toasts';
import { isPresent } from './validations';

export const pdfToBase64 = (response: Object): Promise<string> => new Promise((resolve) => {
  const file = new Blob([response], { type: 'application/pdf' });
  const reader = new window.FileReader();
  reader.readAsDataURL(file);
  reader.onloadend = () =>
    resolve(reader.result.split(',').pop());
});

export const copyToClipboard = (text: any) => {
  const docx: any = document;
  if (window.clipboardData && window.clipboardData.setData) {
    return window.clipboardData.setData('Text', text);
  } else if (docx.queryCommandSupported && docx.queryCommandSupported('copy')) {
    const textarea = docx.createElement('textarea');
    textarea.textContent = text;
    textarea.style.position = 'fixed';
    docx.body.appendChild(textarea);
    textarea.select();
    try {
      return docx.execCommand('copy');
    } catch (ex) {
      // eslint-disable-next-line
      console.warn("Copy to clipboard failed.", ex);
      return false;
    } finally {
      docx.body.removeChild(textarea);
    }
  }
};

export const copyToClipboardEv = (text: string) => copyToClipboard(text);

export const handleSubmitEvent = (selector: string, event: Function) => {
  JQuery(selector).on('keydown', (e): any => {
    if (get(e, 'keyCode') === 13 && (get(document, ['activeElement', 'type']) !== 'textarea')) {
      if (window.__hasSelectOpened) {
        return;
      }
      event();
    }
  });
};

// Stops the keys a control handles itself from reaching keydown handlers further up: handleSubmitEvent's save on Enter
// (jQuery, on an ancestor) and a list row's toggle on Space (React, on the root). It has to be a native listener on
// the control, because both of those run before a React handler on the control could stop them. Returns the cleanup.
export const keepKeysWithin = (node: ?HTMLElement, shouldKeep: (event: Object) => boolean): (() => void) => {
  if (!node) return () => {};
  const listener = (event: Object) => {
    if (shouldKeep(event)) event.stopPropagation();
  };
  node.addEventListener('keydown', listener);
  return () => node.removeEventListener('keydown', listener);
};

// keepKeysWithin as a callback ref, for a class property (`bindX = keepKeysRef(isEnterKey)`, then `ref={this.bindX}`):
// React gives it the node on mount and null on unmount, and each node it gets lets go of the previous one's
export const keepKeysRef = (shouldKeep: (event: Object) => boolean): ((node: ?HTMLElement) => void) => {
  let release = () => {};
  return (node: ?HTMLElement) => {
    release();
    release = keepKeysWithin(node, shouldKeep);
  };
};

// Enter as the keydown listeners that save on it see it: AppointmentModalForm.handleKeyDown and handleSubmitEvent
// test keyCode 13 only, so a guard against them has to stop that too, not only key === 'Enter'
export const isEnterKey = (event: Object): boolean => event.key === 'Enter' || event.keyCode === 13;


export const parseUrlParams = (url: string): any => {
  // get query string from url (optional) or window
  let queryString = url ? url.split('?')[1] : window.location.search.slice(1);

  // we'll store the parameters here
  const obj = {};

  // if query string exists
  if (queryString) {
    // stuff after # is not part of query string, so get rid of it
    // eslint-disable-next-line
    queryString = queryString.split('#')[0];

    // split our query string into its component parts
    const arr = queryString.split('&');

    for (let i = 0; i < arr.length; i++) {
      // separate the keys and the values
      const a = arr[i].split('=');

      // set parameter name and value (use 'true' if empty)
      let paramName = a[0];
      let paramValue = typeof (a[1]) === 'undefined' ? true : a[1];

      // (optional) keep case consistent
      paramName = paramName.toLowerCase();
      if (typeof paramValue === 'string') paramValue = paramValue.toLowerCase();

      // if the paramName ends with square brackets, e.g. colors[] or colors[2]
      if (paramName.match(/\[(\d+)?\]$/)) {
        // create key if it doesn't exist
        const key = paramName.replace(/\[(\d+)?\]/, '');
        if (!obj[key]) obj[key] = [];

        // if it's an indexed array e.g. colors[2]
        if (paramName.match(/\[\d+\]$/)) {
          // get the index value and add the entry at the appropriate position
          // eslint-disable-next-line
          const rExec: any = /\[(\d+)\]/.exec(paramName);
          const index = rExec[1];
          obj[key][index] = paramValue;
        } else {
          // otherwise add the value to the end of the array
          obj[key].push(paramValue);
        }
      } else if (!obj[paramName]) {
        // if it doesn't exist, create property
        obj[paramName] = paramValue;
      } else if (obj[paramName] && typeof obj[paramName] === 'string') {
        // if property does exist and it's a string, convert it to an array
        obj[paramName] = [obj[paramName]];
        obj[paramName].push(paramValue);
      } else {
        // otherwise add the property
        obj[paramName].push(paramValue);
      }
    }
  }

  return obj;
};

export function getExtension(path: string) {
  const basename = path.split(/[\\/]/).pop(); // extract file name from full path ...
  // (supports `\\` and `/` separators)
  const pos = basename.lastIndexOf('.'); // get last position of `.`

  if (basename === '' || pos < 1) { return ''; }

  return basename.slice(pos + 1); // extract extension ignoring `.`
}

export function arrayBufferToBase64(buffer: any) {
  let binary = '';
  const bytes = [].slice.call(new Uint8Array(buffer));
  // eslint-disable-next-line
  bytes.forEach((b) => binary += String.fromCharCode(b));

  return window.btoa(binary);
}

export const matchExtensions = (fileName: string, types: Array<string> = []) => {
  let matches = 0;

  forEach(types, (fType: string) => {
    if (endsWith(String(fileName).toLowerCase(), `.${fType}`)) {
      matches++;
    }
  });

  return matches;
};

export const fileExtensionIsImage = (fileName: string) => matchExtensions(fileName, ['png', 'jpg', 'jpeg', 'jfif']);

export const fileExtensionIsExecutable = (fileName: string) => matchExtensions(fileName, ['exe', 'ipa', 'app', 'osx', 'kext', 'appimage']);

const HEIC_MIME_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
const HEIC_EXTENSION_REGEX = /\.hei[cf]s?$/i;

// `file` is a DOM File, but the local flow libdef shadows `File` with a
// constructor-only class, so `Object` follows house style here.
export const isHeicFile = (file: Object): boolean => {
  const type = String(file.type || '').toLowerCase();
  if (HEIC_MIME_TYPES.indexOf(type) !== -1) return true;
  // Chrome on Windows/Android often reports an empty or generic type for .heic
  const typeIsUnknown = type === '' || type === 'application/octet-stream';
  return typeIsUnknown && HEIC_EXTENSION_REGEX.test(String(file.name || ''));
};

// Whether a file reads as empty: no bytes, or none that can be read (a cloud file that isn't on the device). Where it
// can't be tried (no FileReader), it isn't taken for empty.
export const readsEmpty = (file: Object): Promise<boolean> => new Promise((resolve) => {
  try {
    const reader = new window.FileReader();
    reader.onload = () => resolve(!reader.result || reader.result.byteLength === 0);
    reader.onerror = () => resolve(true);
    reader.readAsArrayBuffer(file);
  } catch (e) {
    resolve(false);
  }
});

// Rename-only re-wrap (no transcoding): the backend thumbnails HEIC bytes fine
// once the name/type say jpeg. Returns the original file when it is not HEIC.
export const normalizeHeicFile = (file: Object): Object => {
  if (!isHeicFile(file)) return file;
  const newName = `${String(file.name || 'image').replace(HEIC_EXTENSION_REGEX, '')}.jpeg`;
  try {
    // W3C_File only: it is the native ctor saved in index.html.ejs before
    // cordova-plugin-file clobbers window.File. Falling back to window.File
    // would hit cordova's File(name, localURL, type, ...), which accepts these
    // arguments without throwing and returns a corrupt object instead of
    // reaching the catch below.
    return new window.W3C_File([file], newName, { type: 'image/jpeg', lastModified: file.lastModified });
  } catch (e) {
    return file; // no usable File ctor: keep the pre-rename behavior (file bucket)
  }
};

export const easyOpenCache = (name: string, cb: (cache: Cache | null) => any) => {
  if (!window.caches) {
    cb(null);
    return;
  }
  window.caches.open(name).then((cache) => {
    cb(cache);
  }).catch(() => cb(null));
};

// The image caches (thumbnails and profile photos) share the origin's storage with the persisted
// store. They only grew until sign-out, so a long Safari session filled it.
export const IMAGE_CACHES = { thumbs: 'attachment-thumbs', profilePhotos: 'patients-profile-images' };
// Full-size photos, cached until 4.16.39 (d246d5eb) and read by nothing since: only sign-out cleared it
const LEGACY_IMAGE_CACHES = ['cached-image-view'];
const IMAGE_CACHE_LIMIT = 1000;
const IMAGE_CACHE_TRIM_EVERY = 50;
const CLEAR_COOLDOWN_MS = 10 * 60 * 1000;
const imageCachePuts = {};
let legacyCachesDropped = false;
let lastCleared = -Infinity;
let storageFullReported = false;

// Safari and Chrome say QuotaExceededError (legacy code 22); old Firefox said NS_ERROR_DOM_QUOTA_REACHED (1014)
export const isQuotaError = (error: any): boolean => (
  ['QuotaExceededError', 'NS_ERROR_DOM_QUOTA_REACHED'].includes(get(error, 'name')) || [22, 1014].includes(get(error, 'code'))
);

const deleteCaches = (names: Array<string>): Promise<number> => Promise
  .all(names.map((name) => window.caches.delete(name).catch(() => false)))
  .then((deleted) => deleted.filter(Boolean).length);

// The storage is full: drop the image caches, which refill from S3, so the store can be saved again.
// At most every 10 minutes, so a storage that stays full doesn't wipe them on every image.
export const clearImageCaches = (error: any): Promise<void> => {
  if (!window.caches || Date.now() - lastCleared < CLEAR_COOLDOWN_MS) return Promise.resolve();
  lastCleared = Date.now();
  const names = [...Object.values(IMAGE_CACHES), ...LEGACY_IMAGE_CACHES];

  return deleteCaches(names)
    .then((deleted) => {
      // Once per page load: a disk that stays full sent one every 10 minutes for as long as the page was open
      if (storageFullReported) return;
      storageFullReported = true;
      // Its own group, apart from the unhandled QuotaExceededError this replaces, to see how often it happens
      const report = new Error(`Image caches cleared (${deleted}/${names.length}): ${get(error, 'name')} ${get(error, 'message', '')}`);
      report.name = 'StorageFull';
      Bugsnag.notify(report, (event) => { event.severity = 'info'; });
    })
    .catch(() => {});
};

// Bugsnag's onError: IndexedDB's full disk, left unhandled, takes the same path as a failed save (the image caches
// cleared, StorageFull once) instead of going out on its own. localforage leaves it unhandled when it can't reopen
// its database ("Encountered full disk while opening backing store for indexedDB.open."). Any other quota error
// (localStorage's own limit, say) still goes to Bugsnag, and so does this one without the Cache API (no StorageFull
// would replace it). True when it took it.
export const handleUnhandledQuotaError = (event: Object): boolean => {
  const error = event.originalError;
  if (!event.unhandled || !window.caches || !isQuotaError(error)) return false;
  if (!/indexeddb|backing store/i.test(String(get(error, 'message', '')))) return false;
  clearImageCaches(error);
  return true;
};

// Keeps the newest. A cache far over the limit (grown since before the limit) is cheaper to start over.
const trimImageCache = (name: string, cache: Cache) => cache.keys().then((keys) => {
  if (keys.length > IMAGE_CACHE_LIMIT * 2) return window.caches.delete(name);
  return Promise.all(keys.slice(0, Math.max(0, keys.length - IMAGE_CACHE_LIMIT)).map((key) => cache.delete(key)));
});

// Keeps an image that was just downloaded for the next visit, without a second download. Call it
// before reading the response's body: it caches a clone. Every so often only the newest are kept.
export const cacheImage = (name: string, url: string, response: Response): Promise<void> => {
  if (!window.caches) return Promise.resolve();
  const copy = response.clone();
  if (!legacyCachesDropped) {
    legacyCachesDropped = true;
    deleteCaches(LEGACY_IMAGE_CACHES);
  }

  // Under the URL without its signature, so storing the image again replaces it (lookups ignore the query)
  return window.caches.open(name)
    .then((cache) => cache.put(String(url).split('?')[0], copy).then(() => {
      imageCachePuts[name] = (imageCachePuts[name] || 0) + 1;
      if (imageCachePuts[name] % IMAGE_CACHE_TRIM_EVERY === 1) return trimImageCache(name, cache);
      return null;
    }))
    .then(() => {})
    .catch((error) => {
      if (isQuotaError(error)) clearImageCaches(error);
    });
};

// An attachment the PDF viewer opens. The extension, when the API sends it, or the name ("LAUDO.PDF" too)
export const isPdfFile = (file: ?{ extension?: ?string, name?: ?string }): boolean => (
  !!file && (String(file.extension).toLowerCase() === 'pdf' || /\.pdf$/i.test(String(file.name)))
);

export const isShareable = (fileName: string) => {
  const allowedShareMimes = ['application/zip', 'application/pdf', 'application/x-rar-compressed', 'application/x-tar'];

  const isPDF = String(fileName).endsWith('.pdf');
  const isVideo = (lookup(fileName) && lookup(fileName).startsWith('video/'));
  const isImage = (lookup(fileName) && lookup(fileName).startsWith('image/'));
  const isAllowedMime = allowedShareMimes.indexOf(lookup(fileName)) !== -1;

  return isPDF || isVideo || isImage || isAllowedMime;
};


type DownloadOrShareSettings = {
  state: any,
  url: string,
  params: any,
  mime: string,
  dispatch: Function,
  // uses {d} to set unix digits in name and {t} for date/time
  fileName: string;
};

export const downloadOrShare = ({
  params, state, url, mime, dispatch, fileName,
}: DownloadOrShareSettings) => new Promise<{shared: boolean, sentByEmail?: boolean}>((resolve, reject) => {
  const urlParams = map<any, any>(params, (value, key) => `${key}=${value}`).join('&');

  const fullUrl = `${url}?${urlParams}`;
  const sharePluginEnabled = window.isCordovaApp && ((!!window.plugins && !!window.plugins.socialsharing) || (!!window.plugins && !!window.plugins.PrintPDF));

  const fullFileName = fileName.replace('{d}', moment().unix().toString()).replace('{t}', moment().format('DD_MM_YYYY'));

  const rejectWithResponseError = (response) => {
    response.json().then((body) => {
      // 422 body: { errors: [{ property, errorMessages: [localized message] }] }
      const message = get(body, ['errors', 0, 'errorMessages', 0]);
      const error: any = new Error(message || `Report request failed (status ${response.status})`);
      error.userMessage = message;
      reject(error);
    }).catch(() => reject(new Error(`Report request failed (status ${response.status})`)));
  };

  fetch(fullUrl, {
    method: 'GET',
    headers: {
      'access-token': get(state, ['auth', 'token'], null),
      clinicid: get(state, ['context', 'clinics', 'activeClinic', 'id'], null),
    },
  }).then((r) => {
    // 204: the backend queued the report to be sent by email — there is no file body
    if (r.status === 204) {
      resolve({ shared: false, sentByEmail: true });
      return null;
    }
    if (!r.ok) {
      rejectWithResponseError(r);
      return null;
    }
    return r.blob();
  }).then((b) => {
    if (!b) {
      return;
    }
    b.arrayBuffer().then((buff) => {
      const blob = new Blob([buff], { type: mime });

      if (sharePluginEnabled) {
        dispatch({
          type: 'toasts.OPEN_TOAST',
          data: {
            type: 'success', text: 'Compartilhando...', close: true, timeout: 10000, id: SHARE_TOAST_ID,
          },
        });
        socialShareFile(blob, isPresent(fullFileName) ? fullFileName : `doc-${(new Date()).getTime() / 1000}.xlsx`, dispatch);
        resolve({ shared: true });
      } else {
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.target = '_blank';
        a.href = objectUrl;
        a.download = fullFileName;
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);

        resolve({ shared: false });
      }
    }).catch(reject);
  }).catch(reject);
});
